import { useState, useEffect } from 'react';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import ImagePreview from '../ImagePreview';
import { UploadInput } from '../UploadInput';
import { Check } from 'lucide-react';
import { toast } from 'sonner';

const VERIFICATION_STEPS = [
  { key: 'bankLogin', label: 'Bank Login', description: 'Verify bank login credentials work correctly' },
  { key: 'accountStatement', label: 'Account Statement', description: 'Verify account statement is accessible' },
  { key: 'creditVerification', label: 'Credit Verification', description: 'Verify credit/deposit transactions work' },
  { key: 'debitVerification', label: 'Debit Verification', description: 'Verify debit/withdrawal transactions work' },
  { key: 'phoneVerification', label: 'Phone Number', description: 'Verify registered phone number is active' },
  { key: 'upiSetup', label: 'UPI ID Setup', description: 'Verify UPI ID is set up and working' },
];

const VerificationRemarks = ({ stepKey, initialValue, onSave }) => {
  const [val, setVal] = useState(initialValue || '');
  useEffect(() => { setVal(initialValue || ''); }, [initialValue]);
  return (
    <Input className="mt-1 text-sm" placeholder="Add remarks..."
      data-testid={`verify-notes-${stepKey}`}
      value={val}
      onChange={(e) => setVal(e.target.value)}
      onBlur={() => { if (val !== (initialValue || '')) onSave(val); }}
    />
  );
};

export const VerificationPanel = ({ bank, vendorId, canEdit, onUpdate, onScreenshotUpload }) => {
  const verification = bank.verification || {};
  const passedCount = VERIFICATION_STEPS.filter(s => verification[s.key]?.verified).length;
  const allPassed = passedCount === VERIFICATION_STEPS.length;

  const handleToggle = async (step, currentData) => {
    // Require screenshot before allowing verification
    if (!currentData.verified && !currentData.screenshotUrl) {
      toast.error(`Please upload a screenshot for "${step.label}" before verifying`);
      return;
    }
    const newVerified = !currentData.verified;
    await onUpdate(step.key, { verified: newVerified, screenshotUrl: currentData.screenshotUrl || '', notes: currentData.notes || '' });
  };

  const handleNotes = async (step, currentData, notes) => {
    await onUpdate(step.key, { verified: currentData.verified, screenshotUrl: currentData.screenshotUrl || '', notes });
  };

  const isEditable = canEdit && (bank.status === 'New Bank' || bank.status === 'In Process');

  return (
    <>
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-semibold text-base">Verification Checklist</h3>
          <p className="text-xs text-slate-500 mt-1">Complete all 6 steps to mark bank as "Unassigned"</p>
        </div>
        <div className="flex items-center gap-2">
          <div className={`text-sm font-bold ${allPassed ? 'text-green-700' : 'text-amber-700'}`}>
            {passedCount}/{VERIFICATION_STEPS.length}
          </div>
          <div className="w-24 h-2 bg-slate-200 rounded-full overflow-hidden">
            <div className={`h-full rounded-full transition-all ${allPassed ? 'bg-green-500' : 'bg-amber-500'}`} style={{ width: `${(passedCount / VERIFICATION_STEPS.length) * 100}%` }} />
          </div>
        </div>
      </div>

      {allPassed && (
        <div className="p-3 bg-green-50 border border-green-200 rounded-lg text-sm text-green-800 font-medium" data-testid="verification-complete-msg">
          All verifications passed! Bank is now "Unassigned".
        </div>
      )}

      <div className="space-y-3">
        {VERIFICATION_STEPS.map((step) => {
          const data = verification[step.key] || { verified: false, screenshotUrl: '', notes: '' };
          return (
            <Card key={step.key} className={`p-4 border ${data.verified ? 'border-green-200 bg-green-50/30' : 'border-slate-200'}`} data-testid={`verification-step-${step.key}`}>
              <div className="flex items-start gap-3">
                <button
                  onClick={() => isEditable && handleToggle(step, data)}
                  disabled={!isEditable}
                  className={`mt-0.5 flex-shrink-0 w-6 h-6 rounded-md border-2 flex items-center justify-center transition-colors ${
                    data.verified ? 'bg-green-500 border-green-500 text-white' : !data.screenshotUrl && isEditable ? 'border-slate-200 bg-slate-50 cursor-not-allowed' : 'border-slate-300 hover:border-green-400'
                  } ${!isEditable ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                  data-testid={`verify-check-${step.key}`}
                  title={!data.screenshotUrl && !data.verified ? 'Upload screenshot first' : ''}
                >
                  {data.verified && <Check className="w-4 h-4" />}
                </button>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="font-semibold text-sm">{step.label}</span>
                      <p className="text-xs text-slate-500">{step.description}</p>
                    </div>
                    <Badge className={data.verified ? 'bg-green-100 text-green-800' : 'bg-slate-100 text-slate-600'}>
                      {data.verified ? 'Verified' : 'Pending'}
                    </Badge>
                  </div>

                  <div className="mt-3 grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs text-slate-500">Screenshot</Label>
                      {data.screenshotUrl ? (
                        <div className="mt-1">
                          <ImagePreview imageUrl={data.screenshotUrl} label={step.label} />
                        </div>
                      ) : (
                        <div className="mt-1 text-xs text-slate-400 italic">No screenshot uploaded</div>
                      )}
                      {isEditable && (
                        <UploadInput className="mt-2 text-xs" data-testid={`verify-upload-${step.key}`}
                          allowPdf={false}
                          onFileUpload={async (file) => {
                            await onScreenshotUpload(step.key, file, data.notes || '');
                          }} />
                      )}
                    </div>
                    <div>
                      <Label className="text-xs text-slate-500">Remarks</Label>
                      {isEditable ? (
                        <VerificationRemarks
                          stepKey={step.key}
                          initialValue={data.notes}
                          onSave={(val) => handleNotes(step, data, val)}
                        />
                      ) : (
                        <div className="mt-1 text-sm text-slate-700">{data.notes || '-'}</div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </Card>
          );
        })}
      </div>
    </>
  );
};
